import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import api from '../api';
import TaskForm from '../components/TaskForm';
import TaskDetailModal from '../components/TaskDetailModal';
import { Calendar as CalendarIcon, Plus, AlertTriangle, Clock, CheckCircle, ChevronRight } from 'lucide-react';

// Ista logika kao u Dashboard.js
function getTaskWorkStatus(task) {
  if (task.status === 'done') return 'done';
  const assignments = task.assignments || [];
  if (assignments.length === 0) return 'pending';
  if (assignments.some(a => a.work_status === 'in_progress' || a.work_status === 'done')) return 'in_progress';
  return 'pending';
}

function getEventColor(task) {
  const now = new Date();
  const endDate = task.end_date ? new Date(task.end_date) : null;
  const startDate = new Date(task.start_date);
  const isPast = startDate < now;
  const isOverdue = isPast && task.status !== 'done' && endDate && endDate < now;
  const workStatus = getTaskWorkStatus(task);

  if (task.status === 'done') return { bg: '#00b894', border: '#00a381' };
  if (isOverdue) return { bg: '#d63031', border: '#c0392b' };
  if (workStatus === 'in_progress') return { bg: '#0984e3', border: '#0773c5' };
  if (task.is_recurring) return { bg: '#6c5ce7', border: '#5a4bd1' };
  if (task.status === 'proposed') return { bg: '#fdcb6e', border: '#e6b800' };
  return { bg: '#1a2a4a', border: '#0f1d35' };
}

function NextTaskBanner({ tasks, userEmail, isAdmin }) {
  const now = new Date();
  const upcomingTasks = tasks
    .filter(task => {
      const taskDate = new Date(task.start_date);
      if (taskDate <= now) return false;
      if (isAdmin) return true;
      return task.assignments?.some(a => a.email === userEmail);
    })
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

  if (upcomingTasks.length === 0) {
    return (
      <div style={{
        background: 'white', borderRadius: 12, padding: '14px 18px', marginBottom: 16,
        borderTop: '1px solid #eef0f3', borderRight: '1px solid #eef0f3',
        borderBottom: '1px solid #eef0f3', borderLeft: '5px solid #dfe6e9',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        fontSize: 16, color: '#8395a7', display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <CalendarIcon size={16} color="#b2bec3" /> Nema nadolazećih zadataka.
      </div>
    );
  }

  const next = upcomingTasks[0];
  const taskDate = new Date(next.start_date);
  const diffMs = taskDate - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  let timeText;
  if (diffMinutes < 60) timeText = `za ${diffMinutes} minuta`;
  else if (diffHours < 24) timeText = `za ${diffHours} ${diffHours === 1 ? 'sat' : diffHours < 5 ? 'sata' : 'sati'}`;
  else if (diffDays === 1) timeText = 'sutra';
  else if (diffDays < 7) timeText = `za ${diffDays} dana`;
  else if (diffDays < 30) { const weeks = Math.floor(diffDays / 7); timeText = `za ${weeks} ${weeks === 1 ? 'tjedan' : 'tjedna'}`; }
  else { const months = Math.floor(diffDays / 30); timeText = `za ${months} ${months === 1 ? 'mjesec' : months < 5 ? 'mjeseca' : 'mjeseci'}`; }

  const myAssignment = next.assignments?.find(a => a.email === userEmail);
  const isMandatory = myAssignment?.is_mandatory;

  const borderColor = diffDays <= 1 ? '#d63031' : diffDays <= 7 ? '#0984e3' : '#00b894';
  const icon = diffDays <= 1
    ? <AlertTriangle size={16} color="#d63031" />
    : diffDays <= 7
    ? <Clock size={16} color="#0984e3" />
    : <CheckCircle size={16} color="#00b894" />;

  return (
    <div style={{
      background: 'white', borderRadius: 12, padding: '14px 18px', marginBottom: 16,
      borderTop: '1px solid #eef0f3', borderRight: '1px solid #eef0f3',
      borderBottom: '1px solid #eef0f3', borderLeft: `5px solid ${borderColor}`,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {icon}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#b2bec3', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
            Sljedeći zadatak
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1e2d3d' }}>
            {next.title}
            {isMandatory && (
              <span style={{ marginLeft: 8, background: '#fde8e8', color: '#d63031', padding: '2px 7px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>Obavezno</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#8395a7', marginTop: 2 }}>
            {timeText} · {taskDate.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
      {upcomingTasks.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#b2bec3' }}>
          + još {upcomingTasks.length - 1} {upcomingTasks.length - 1 === 1 ? 'zadatak' : 'zadataka'}
          <ChevronRight size={14} />
        </div>
      )}
    </div>
  );
}

// Legenda boja
function ColorLegend() {
  const items = [
    { color: '#1a2a4a', label: 'Nije započeto' },
    { color: '#0984e3', label: 'U tijeku' },
    { color: '#00b894', label: 'Završeno' },
    { color: '#d63031', label: 'Kasni' },
    { color: '#6c5ce7', label: 'Ponavljajući' },
    { color: '#fdcb6e', label: 'Na odobrenju' },
  ];
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
      {items.map(item => (
        <div key={item.color} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#636e72' }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color, flexShrink: 0 }} />
          {item.label}
        </div>
      ))}
    </div>
  );
}

function CalendarPage({ user }) {
  const [tasks, setTasks] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const isMobile = window.innerWidth <= 768;

  useEffect(() => { fetchTasks(); }, []);

  const fetchTasks = async () => {
    try {
      const res = await api.get('/tasks');
      setTasks(res.data);
    } catch (err) { console.error(err); }
  };

  const calendarEvents = tasks.map(task => {
    const colors = getEventColor(task);
    return {
      id: task.id,
      title: task.title,
      start: task.start_date,
      end: task.end_date,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      textColor: 'white',
      extendedProps: { task },
    };
  });

  const handleDateClick = (info) => {
    if (user.role !== 'admin') return;
    setSelectedDate(info.dateStr);
    setShowCreateModal(true);
  };

  const handleEventClick = (info) => {
    setSelectedTask(info.event.extendedProps.task);
    setShowDetailModal(true);
  };

  return (
    <div>
      <NextTaskBanner tasks={tasks} userEmail={user.email} isAdmin={user.role === 'admin'} />

      <div style={{
        background: 'white', borderRadius: 12, padding: isMobile ? 12 : 24,
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        border: '1px solid #eef0f3',
      }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarIcon size={18} color="#1a2a4a" />
            <h2 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1a2a4a', margin: 0 }}>Kalendar zadataka</h2>
          </div>
          {user.role === 'admin' && (
            <button
              className="btn btn-primary"
              style={{ fontSize: 14, padding: '7px 14px', background: '#1a2a4a', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              onClick={() => { setSelectedDate(null); setShowCreateModal(true); }}
            >
              <Plus size={14} /> Novi zadatak
            </button>
          )}
        </div>

        <ColorLegend />

        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          initialView={isMobile ? 'listMonth' : 'dayGridMonth'}
          headerToolbar={isMobile ? {
            left: 'prev,next',
            center: 'title',
            right: 'listMonth,dayGridMonth',
          } : {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek',
          }}
          events={calendarEvents}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          locale="hr"
          buttonText={{
            today: 'Danas',
            month: 'Mjesec',
            week: 'Tjedan',
            list: 'Lista',
            listMonth: 'Lista',
            dayGridMonth: 'Mreža',
          }}
          height="auto"
          contentHeight="auto"
          aspectRatio={isMobile ? 1.2 : 1.8}
          listDayFormat={{ weekday: 'long', day: 'numeric', month: 'long' }}
          listDaySideFormat={false}
          noEventsText="Nema zadataka"
          eventDisplay="block"
        />
      </div>

      {showCreateModal && (
        <TaskForm
          initialDate={selectedDate}
          user={user}
          onClose={() => setShowCreateModal(false)}
          onSaved={() => { fetchTasks(); setShowCreateModal(false); }}
        />
      )}

      {showDetailModal && selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          user={user}
          onClose={() => setShowDetailModal(false)}
          onUpdate={() => { fetchTasks(); setShowDetailModal(false); }}
        />
      )}
    </div>
  );
}

export { CalendarPage as default };
